// src/features/bom/pages/ProductionFloorFormPage.tsx

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    getProductionFloor, createProductionFloor, updateProductionFloor,
    ProductionFloor,
} from '../services/productionFloorApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft, Save, Factory, ChefHat, Beaker, Utensils,
    Wine, Warehouse, Building2, User, Phone, Mail, FileText,
    MapPin,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { extractApiError } from '@/lib/api-error';

const FLOOR_TYPES = [
    { value: 'kitchen', label: 'Kitchen', icon: ChefHat, color: 'from-amber-500 to-orange-600' },
    { value: 'bakery', label: 'Bakery', icon: Beaker, color: 'from-rose-400 to-pink-600' },
    { value: 'butchery', label: 'Butchery', icon: Utensils, color: 'from-red-500 to-rose-600' },
    { value: 'bar', label: 'Bar', icon: Wine, color: 'from-purple-500 to-indigo-600' },
    { value: 'general', label: 'General', icon: Warehouse, color: 'from-slate-500 to-slate-700' },
];

export function ProductionFloorFormPage() {
    const navigate = useNavigate();
    const { floorId } = useParams<{ floorId: string }>();
    const isEdit = !!floorId;
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [form, setForm] = useState({
        name: '',
        branchId: '',
        floorType: 'general',
        manager: '',
        description: '',
        contactPhone: '',
        contactEmail: '',
    });

    // Load branches
    const { data: branches } = useQuery({
        queryKey: ['branches', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    // Load existing floor if editing
    const { data: existingFloor, isLoading: floorLoading } = useQuery({
        queryKey: ['production-floor', floorId],
        queryFn: () => getProductionFloor(user!.tenantId, floorId!),
        enabled: !!floorId && !!user?.tenantId,
    });

    useEffect(() => {
        if (existingFloor) {
            setForm({
                name: existingFloor.name,
                branchId: existingFloor.branchId,
                floorType: existingFloor.floorType,
                manager: existingFloor.manager || '',
                description: existingFloor.description || '',
                contactPhone: existingFloor.contactPhone || '',
                contactEmail: existingFloor.contactEmail || '',
            });
        }
    }, [existingFloor]);

    // Auto-select first branch if creating new
    useEffect(() => {
        if (!isEdit && branches && branches.length > 0 && !form.branchId) {
            setForm(prev => ({ ...prev, branchId: branches[0].id }));
        }
    }, [branches, isEdit]);

    const saveMutation = useMutation({
        mutationFn: () => {
            const payload = {
                name: form.name,
                branchId: form.branchId,
                floorType: form.floorType,
                manager: form.manager || undefined,
                description: form.description || undefined,
                contactPhone: form.contactPhone || undefined,
                contactEmail: form.contactEmail || undefined,
            };

            return isEdit
                ? updateProductionFloor(user!.tenantId, floorId!, payload)
                : createProductionFloor(user!.tenantId, payload);
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['production-floors'] });
            addToast({
                type: 'success',
                title: isEdit ? 'Floor Updated' : 'Floor Created',
                message: `"${result.name}" saved successfully.`,
            });
            navigate('/bom/production-floors');
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Save Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) {
            addToast({ type: 'error', title: 'Name Required', message: 'Please enter a floor name.' });
            return;
        }
        if (!form.branchId) {
            addToast({ type: 'error', title: 'Branch Required', message: 'Please select a branch.' });
            return;
        }
        saveMutation.mutate();
    };

    const updateField = (field: string, value: string) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    if (isEdit && floorLoading) {
        return <div className="min-h-screen flex items-center justify-center"><Spinner size="xl" /></div>;
    }

    if (isEdit && !existingFloor && !floorLoading) {
        return <ErrorState title="Production floor not found" onRetry={() => navigate('/bom/production-floors')} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/bom/production-floors')} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg">
                            <Factory className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">
                                {isEdit ? 'Edit Production Floor' : 'New Production Floor'}
                            </h1>
                            <p className="text-sm text-slate-500">
                                {isEdit ? form.name || 'Loading...' : 'Set up a new production area'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto p-6">
                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader gradient>
                            <div className="flex items-center gap-3">
                                <Factory className="w-5 h-5 text-amber-600" />
                                <div>
                                    <h3 className="font-semibold text-slate-800">Floor Information</h3>
                                    <p className="text-xs text-slate-500">Basic details about this production floor</p>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 space-y-4">
                            {/* Name */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Floor Name <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    placeholder="e.g., Main Kitchen, Bakery Section"
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                    required
                                />
                            </div>

                            {/* Branch & Type */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Branch <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={form.branchId}
                                        onChange={(e) => updateField('branchId', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        required
                                    >
                                        <option value="">Select branch...</option>
                                        {(branches || []).map((b: any) => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Floor Type
                                    </label>
                                    <select
                                        value={form.floorType}
                                        onChange={(e) => updateField('floorType', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                    >
                                        {FLOOR_TYPES.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Manager */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Manager
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        value={form.manager}
                                        onChange={(e) => updateField('manager', e.target.value)}
                                        placeholder="e.g., Chef John Doe"
                                        className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                    />
                                </div>
                            </div>

                            {/* Contact */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Phone
                                    </label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            value={form.contactPhone}
                                            onChange={(e) => updateField('contactPhone', e.target.value)}
                                            placeholder="+234..."
                                            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="email"
                                            value={form.contactEmail}
                                            onChange={(e) => updateField('contactEmail', e.target.value)}
                                            placeholder="kitchen@example.com"
                                            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                    Description
                                </label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => updateField('description', e.target.value)}
                                    rows={3}
                                    placeholder="Describe what happens on this floor..."
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Floor Type Preview */}
                    <Card className="mb-6">
                        <CardContent className="p-6">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Floor Type Preview</p>
                            <div className="flex flex-wrap gap-3">
                                {FLOOR_TYPES.map(type => (
                                    <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => updateField('floorType', type.value)}
                                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${form.floorType === type.value
                                            ? `border-amber-400 bg-gradient-to-r ${type.color} text-white shadow-lg`
                                            : 'border-slate-200 bg-white text-slate-600 hover:border-amber-200'
                                            }`}
                                    >
                                        <type.icon size={18} />
                                        <span className="text-sm font-semibold">{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => navigate('/bom/production-floors')}
                            className="px-5 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saveMutation.isPending}
                            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50"
                        >
                            {saveMutation.isPending ? (
                                <Spinner size="xs" variant="white" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {isEdit ? 'Update Floor' : 'Create Floor'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}